import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Split, Users, Filter as FilterIcon, AlertTriangle, CheckCircle, X, Building2 } from "lucide-react";
import { toast } from "sonner";
import { fetchAvailableUsers, fetchUsers, searchUsers } from "../../services/crmServices/userService";
import { fetchDesks, fetchAvailableDesks } from "../../services/crmServices/deskService";
import { shuffleLeads, shufflePreview } from "../../services/crmServices/leadService";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { useAuth } from "../../hooks/useAuth";
import { MultiSelect } from "../ui/multi-select";

export function ShuffleLeadsModal({ open, onClose, onSuccess, selectedLeads, allLeads }) {
    const { user } = useAuth();
    const [shuffleType, setShuffleType] = useState("agents"); // "agents" or "desks"
    const [agents, setAgents] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [desks, setDesks] = useState([]);
    const [selectedDesks, setSelectedDesks] = useState([]);
    const [deskAgents, setDeskAgents] = useState({}); // Map of deskId -> agents in that desk
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedAffiliate, setSelectedAffiliate] = useState("");
    const [affiliates, setAffiliates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [desksLoading, setDesksLoading] = useState(false);
    const [filteredLeads, setFilteredLeads] = useState([]);
    // Progress tracking state
    const [shuffleProgress, setShuffleProgress] = useState({ current: 0, total: 0, percentage: 0 });
    // Actual count from backend when using "Select All"
    const [actualCount, setActualCount] = useState(null);
    const [countLoading, setCountLoading] = useState(false);

    const leadStatuses = useSelector(selectLeadStatuses);
    const isFloorManager = user?.position?.role === "floorManager";
    useEffect(() => {
        if (open) {
            if (shuffleType === "desks" && isFloorManager) {
                fetchDesksForShuffle();
            } else {
                fetchAgents();
            }
            extractAffiliates();
            // Set all statuses as selected by default
            setSelectedStatuses(leadStatuses.map((status) => status.value));
            // Reset actual count when opening
            setActualCount(null);
            setCountLoading(false);
        }
    }, [open, leadStatuses]);

    useEffect(() => {
        // When shuffle type changes while modal is open, fetch appropriate data
        if (open) {
            if (shuffleType === "desks" && isFloorManager) {
                fetchDesksForShuffle();
            } else if (shuffleType === "agents") {
                fetchAgents();
            }
        }
    }, [shuffleType, isFloorManager, open]);

    useEffect(() => {
        // When shuffle type changes, reset selections
        if (shuffleType === "desks") {
            setSelectedAgents([]);
            if (selectedDesks.length > 0) {
                fetchAgentsFromDesks();
            }
        } else {
            setSelectedDesks([]);
            setDeskAgents({});
        }
    }, [shuffleType]);

    useEffect(() => {
        // When desks are selected, extract agents from loaded desks
        if (shuffleType === "desks" && selectedDesks.length > 0 && desks.length > 0) {
            extractAgentsFromDesks(desks);
        }
    }, [selectedDesks, desks]);

    useEffect(() => {
        // Note: Status and affiliate filtering is now handled by the backend
        // This ensures it works correctly even when selectedLeads only contains IDs (from "Select All")
        // Frontend filtering is only for display purposes to show accurate counts
        
        let filtered = [...selectedLeads];

        // Only attempt frontend filtering if leads have full data (not just IDs)
        const hasFullData = selectedLeads.length > 0 && selectedLeads[0].status !== undefined;
        
        if (hasFullData) {
            if (selectedStatuses.length > 0) {
                // Normalize selected statuses for comparison (trim and lowercase to handle case differences)
                const normalizedSelectedStatuses = selectedStatuses
                    .map(s => s ? s.toString().trim().toLowerCase() : '')
                    .filter(s => s);
                
                filtered = filtered.filter((lead) => {
                    if (!lead || !lead.status) return false;
                    
                    // Handle status as either string or object with value property
                    const leadStatus = lead.status?.value || lead.status;
                    if (!leadStatus) return false;
                    
                    // Normalize lead status for case-insensitive comparison
                    const normalizedLeadStatus = leadStatus.toString().trim().toLowerCase();
                    return normalizedSelectedStatuses.includes(normalizedLeadStatus);
                });
            }

            if (selectedAffiliate) {
                filtered = filtered.filter((lead) => lead.affiliate === selectedAffiliate);
            }
        } else if (selectedLeads.length > 0 && selectedStatuses.length > 0) {
            // For "Select All" case, fetch actual count from backend
            fetchActualCount();
        }

        setFilteredLeads(filtered);
    }, [selectedStatuses, selectedAffiliate, selectedLeads]);

    // Fetch actual count from backend when using "Select All" with status filters
    const fetchActualCount = async () => {
        if (selectedLeads.length === 0 || selectedStatuses.length === 0) {
            setActualCount(null);
            return;
        }

        setCountLoading(true);
        try {
            const previewData = {
                leadIds: selectedLeads.map(l => l._id),
                statuses: selectedStatuses,
                affiliate: selectedAffiliate || null
            };
            
            const response = await shufflePreview(previewData);

            if (response?.status === 200) {
                setActualCount(response.count || 0);
            } else {
                setActualCount(null);
            }
        } catch (error) {
            console.error('Error fetching actual count:', error);
            setActualCount(null);
        } finally {
            setCountLoading(false);
        }
    };

    const fetchAgents = async () => {
        setAgentsLoading(true);
        try {
            const response = await fetchAvailableUsers();
            if (response?.data) {
                // Filter for agents only
                const agentUsers = response.data.filter((user) => user.position?.role === "agent");
                setAgents(agentUsers);
            }
        } catch (error) {
            console.error("Error fetching agents:", error);
            toast.error("Failed to load agents");
        } finally {
            setAgentsLoading(false);
        }
    };

    const fetchDesksForShuffle = async () => {
        setDesksLoading(true);
        try {
            // Use fetchAvailableDesks for floor managers to get properly filtered desks
            // This endpoint already filters desks by floorManager on the backend
            const response = user?.position?.role === "floorManager" 
                ? await fetchAvailableDesks()
                : await fetchDesks();
            
            if (response?.data) {
                setDesks(response.data);
                // Extract agents from desks (desks from fetchAvailableDesks already include agents)
                extractAgentsFromDesks(response.data);
            }
        } catch (error) {
            console.error("Error fetching desks:", error);
            toast.error("Failed to load desks");
        } finally {
            setDesksLoading(false);
        }
    };

    // Extract agents from desks that are already loaded (desks from fetchAvailableDesks include agents)
    const extractAgentsFromDesks = (desksData) => {
        const agentsMap = {};
        desksData.forEach(desk => {
            // Normalize desk ID to string for consistent key matching
            const deskId = desk._id?.toString() || desk._id;
            
            if (desk.agents && Array.isArray(desk.agents) && desk.agents.length > 0) {
                // Filter for agents only - users assigned to desk via agentDesk should be agents
                // But we also check position role if available
                const agentUsers = desk.agents.filter((user) => {
                    // If position is populated and has role, check it
                    if (user.position) {
                        const role = user.position?.role || (typeof user.position === 'object' ? user.position.role : null);
                        // If role exists and is not "agent", exclude
                        if (role && role !== "agent") {
                            return false;
                        }
                    }
                    // If no position info, assume agent (since they're in agentDesk)
                    return true;
                });
                agentsMap[deskId] = agentUsers;
            } else {
                agentsMap[deskId] = [];
            }
        });
        setDeskAgents(agentsMap);
    };

    const fetchAgentsFromDesks = async () => {
        const agentsMap = {};
        try {
            // First, try to use agents already in desks if available
            if (desks.length > 0) {
                extractAgentsFromDesks(desks);
                return;
            }

            // Fallback: Batch fetch agents from all selected desks if desks not loaded yet
            if (selectedDesks.length > 0) {
                // Convert desk IDs to ObjectId strings for query
                const deskIdStrings = selectedDesks.map(id => id?.toString());
                
                // Fetch all agents from selected desks in one query
                const response = await searchUsers({ 
                    agentDesk: { $in: deskIdStrings }
                });
                if (response?.data) {
                    // Filter for agents only and group by desk
                    const agentUsers = response.data.filter((user) => {
                        // Handle both populated and non-populated position
                        const role = user.position?.role || (user.position && typeof user.position === 'object' ? user.position.role : null);
                        return role === "agent";
                    });
                    
                    // Group agents by desk
                    selectedDesks.forEach(deskId => {
                        agentsMap[deskId] = agentUsers.filter(agent => {
                            // Handle both populated and non-populated agentDesk
                            const agentDeskId = agent.agentDesk?._id || agent.agentDesk;
                            return agentDeskId?.toString() === deskId?.toString();
                        });
                    });
                }
            }
            setDeskAgents(agentsMap);
        } catch (error) {
            console.error("Error fetching agents from desks:", error);
            toast.error("Failed to load agents from desks");
        }
    };

    const extractAffiliates = () => {
        const uniqueAffiliates = [...new Set(selectedLeads.map((lead) => lead.affiliate).filter(Boolean))];
        setAffiliates(uniqueAffiliates);
    };

    const handleAgentToggle = (agentId) => {
        setSelectedAgents((prev) =>
            prev.includes(agentId) ? prev.filter((id) => id !== agentId) : [...prev, agentId]
        );
    };

    const handleStatusToggle = (statusValue) => {
        setSelectedStatuses((prev) =>
            prev.includes(statusValue) ? prev.filter((status) => status !== statusValue) : [...prev, statusValue]
        );
    };

    const handleSelectAllAgents = () => {
        if (selectedAgents.length === agents.length) {
            setSelectedAgents([]);
        } else {
            setSelectedAgents(agents.map((agent) => agent._id));
        }
    };

    const handleDeskToggle = (deskId) => {
        setSelectedDesks((prev) =>
            prev.includes(deskId) ? prev.filter((id) => id !== deskId) : [...prev, deskId]
        );
    };

    const handleSelectAllDesks = () => {
        if (selectedDesks.length === desks.length) {
            setSelectedDesks([]);
        } else {
            setSelectedDesks(desks.map((desk) => desk._id));
        }
    };

    const handleSelectAllStatuses = () => {
        if (selectedStatuses.length === leadStatuses.length) {
            setSelectedStatuses([]);
        } else {
            setSelectedStatuses(leadStatuses.map((status) => status.value));
        }
    };

    const handleShuffle = async () => {
        if (shuffleType === "agents") {
            if (selectedAgents.length < 2) {
                toast.error("Please select at least 2 agents to split leads");
                return;
            }
        } else {
            if (selectedDesks.length < 2) {
                toast.error("Please select at least 2 desks to split leads");
                return;
            }
            // Check if selected desks have agents
            const allAgentsFromDesks = Object.values(deskAgents).flat();
            if (allAgentsFromDesks.length === 0) {
                toast.error("Selected desks must have at least one agent");
                return;
            }
        }

        if (selectedLeads.length === 0) {
            toast.error("No leads selected for splitting");
            return;
        }

        if (selectedStatuses.length === 0) {
            toast.error("Please select at least one status to split");
            return;
        }
        
        // Validate filtered count
        const hasFullData = selectedLeads.length > 0 && selectedLeads[0].status !== undefined;
        
        if (hasFullData && filteredLeads.length === 0) {
            toast.error("No leads match the selected status/affiliate criteria");
            return;
        }
        
        // For "Select All", check actual count from backend
        if (!hasFullData && actualCount === 0) {
            toast.error("No leads match the selected status criteria");
            return;
        }

        setLoading(true);
        // Use selectedLeads count for "Select All", filteredLeads count when we have full data
        const totalLeads = hasFullData ? filteredLeads.length : selectedLeads.length;
        const startTime = Date.now();
        setShuffleProgress({ current: 0, total: totalLeads, percentage: 0 });

        // Show progress toast
        const progressToast = toast.loading(`Preparing to split ${totalLeads.toLocaleString()} leads...`);

        try {
            // IMPORTANT: Always send ALL selectedLeads IDs to backend
            // Backend will filter by status/affiliate, not frontend
            // This ensures accurate filtering even with "Select All"
            const shuffleData = {
                shuffleType: shuffleType,
                agentIds: shuffleType === "agents" ? selectedAgents : null,
                deskIds: shuffleType === "desks" ? selectedDesks : null,
                leadIds: selectedLeads.map((lead) => lead._id), // Use selectedLeads, not filteredLeads!
                statuses: selectedStatuses,
                affiliate: selectedAffiliate || null
            };

            // Enhanced progress simulation with time estimation
            let progressStep = 0;
            const progressInterval = setInterval(() => {
                setShuffleProgress(prev => {
                    progressStep++;
                    // Smooth progress curve: fast at start, slower near end
                    const progress = Math.min(95, Math.round(100 * (1 - Math.exp(-progressStep / 8))));
                    const currentCount = Math.round((progress / 100) * totalLeads);
                    const elapsed = Math.round((Date.now() - startTime) / 1000);
                    const estimated = progress > 5 ? Math.round((elapsed / progress) * 100) : 0;
                    const remaining = Math.max(0, estimated - elapsed);
                    
                    toast.loading(
                        `Splitting ${currentCount.toLocaleString()} / ${totalLeads.toLocaleString()} leads (${progress}%) • ${remaining}s remaining`,
                        { id: progressToast }
                    );
                    return { current: currentCount, total: totalLeads, percentage: progress };
                });
            }, 300);

            const response = await shuffleLeads(shuffleData);

            clearInterval(progressInterval);
            const elapsed = Math.round((Date.now() - startTime) / 1000);
            toast.dismiss(progressToast);
            setShuffleProgress({ current: totalLeads, total: totalLeads, percentage: 100 });

            if (response?.status === 200) {
                const actualCount = response?.data?.count;
                
                if (!actualCount || actualCount === 0) {
                    toast.error("No leads matched the selected status criteria");
                    setLoading(false);
                    setShuffleProgress({ current: 0, total: 0, percentage: 0 });
                    return;
                }
                
                const shuffledLeadsText = actualCount.toLocaleString();
                const timeText = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
                
                const successMessage = shuffleType === "agents"
                    ? `✓ Successfully split ${shuffledLeadsText} leads among ${selectedAgents.length} agents in ${timeText}`
                    : `✓ Successfully split ${shuffledLeadsText} leads among ${selectedDesks.length} desks in ${timeText}`;
                    
                toast.success(successMessage, { duration: 5000 });
                onSuccess();
                handleClose();
            } else {
                toast.error("Failed to split leads");
            }
        } catch (error) {
            toast.dismiss(progressToast);
            console.error("Error shuffling leads:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to split leads";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setShuffleProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleClose = () => {
        setSelectedAgents([]);
        setSelectedDesks([]);
        setSelectedStatuses([]);
        setSelectedAffiliate("");
        setShuffleType("agents");
        setDeskAgents({});
        setActualCount(null);
        setCountLoading(false);
        onClose();
    };

    const getStatusConfig = (statusValue) => {
        return leadStatuses.find((status) => status.value === statusValue);
    };

    // Calculate leads distribution per agent
    const leadsDistribution = useMemo(() => {
        if (shuffleType !== "agents" || selectedAgents.length === 0) {
            return null;
        }

        const hasFullData = selectedLeads.length > 0 && selectedLeads[0].status !== undefined;
        const totalLeads = hasFullData ? filteredLeads.length : (actualCount !== null ? actualCount : selectedLeads.length);
        
        if (totalLeads === 0) return null;

        const leadsPerAgent = Math.floor(totalLeads / selectedAgents.length);
        const remainder = totalLeads % selectedAgents.length;
        
        // Distribute leads evenly, with remainder going to first agents
        const distribution = selectedAgents.map((agentId, index) => {
            const agent = agents.find(a => a._id === agentId);
            const count = leadsPerAgent + (index < remainder ? 1 : 0);
            return {
                agentId,
                agentName: agent ? `${agent.firstName} ${agent.lastName}` : "Unknown",
                count
            };
        });

        return distribution;
    }, [shuffleType, selectedAgents, selectedLeads, filteredLeads, actualCount, agents]);

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
                <DialogHeader className="flex-shrink-0 border-b pb-4">
                    <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center">
                        <Split className="h-6 w-6 mr-3 text-blue-600" />
                        Split Leads
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        {shuffleType === "agents" 
                            ? "Distribute leads among selected agents based on chosen filters"
                            : "Distribute leads among selected desks (leads will be assigned to agents within those desks)"}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 overflow-y-auto flex-1 pr-2 -mr-2 pt-2">
                    {/* Shuffle Type Selection (for Floor Managers) */}
                    {isFloorManager && (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base">Split Type</CardTitle>
                                <CardDescription>Choose whether to split between agents or desks</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <RadioGroup value={shuffleType} onValueChange={setShuffleType}>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="agents" id="shuffle-agents" />
                                        <Label htmlFor="shuffle-agents" className="text-sm font-normal cursor-pointer">
                                            Split between Agents
                                        </Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="desks" id="shuffle-desks" />
                                        <Label htmlFor="shuffle-desks" className="text-sm font-normal cursor-pointer">
                                            Split between Desks
                                        </Label>
                                    </div>
                                </RadioGroup>
                            </CardContent>
                        </Card>
                    )}

                    {/* Agents or Desks Selection */}
                    {shuffleType === "agents" ? (
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center">
                                        <Users className="h-4 w-4 mr-2" />
                                        Select Agents ({selectedAgents.length})
                                    </CardTitle>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSelectAllAgents}
                                        disabled={agentsLoading}
                                    >
                                        {selectedAgents.length === agents.length ? "Deselect All" : "Select All"}
                                    </Button>
                                </div>
                                <CardDescription>Choose at least 2 agents to split leads between</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {agentsLoading ? (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                        <span className="ml-2 text-sm text-gray-500">Loading agents...</span>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <Label>Select Agents</Label>
                                            {selectedAgents.length > 0 && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setSelectedAgents([])}
                                                    className="h-6 text-xs text-gray-500 hover:text-gray-700"
                                                >
                                                    <X className="h-3 w-3 mr-1" />
                                                    Clear
                                                </Button>
                                            )}
                                        </div>
                                        <MultiSelect
                                            options={agents.map((agent) => ({
                                                label: `${agent.firstName} ${agent.lastName} (${agent.position?.name || agent.position?.role || 'N/A'})`,
                                                value: agent._id
                                            }))}
                                            selected={selectedAgents}
                                            onChange={(selected) => setSelectedAgents(selected)}
                                            placeholder="Select agents to split leads between"
                                            searchPlaceholder="Search agents..."
                                        />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ) : (
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-base flex items-center">
                                        <Building2 className="h-4 w-4 mr-2" />
                                        Select Desks ({selectedDesks.length})
                                    </CardTitle>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSelectAllDesks}
                                        disabled={desksLoading}
                                    >
                                        {selectedDesks.length === desks.length ? "Deselect All" : "Select All"}
                                    </Button>
                                </div>
                                <CardDescription>Choose at least 2 desks to split leads between</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {desksLoading ? (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                        <span className="ml-2 text-sm text-gray-500">Loading desks...</span>
                                    </div>
                                ) : desks.length === 0 ? (
                                    <div className="flex items-center justify-center py-8 text-center">
                                        <div>
                                            <Building2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-sm text-gray-500">
                                                {user?.position?.role === "floorManager" 
                                                    ? "No desks are assigned to you as floor manager."
                                                    : "No desks available."}
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {desks.map((desk) => {
                                                // Normalize desk ID to match the key used in deskAgents map
                                                const deskId = desk._id?.toString() || desk._id;
                                                const agentsInDesk = deskAgents[deskId] || [];
                                                return (
                                                    <div key={desk._id} className="flex items-start space-x-2">
                                                        <Checkbox
                                                            id={`desk-${desk._id}`}
                                                            checked={selectedDesks.includes(desk._id)}
                                                            onCheckedChange={() => handleDeskToggle(desk._id)}
                                                        />
                                                        <label htmlFor={`desk-${desk._id}`} className="text-sm cursor-pointer flex-1">
                                                            <div className="font-medium">{desk.name}</div>
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                {agentsInDesk.length} agent{agentsInDesk.length !== 1 ? "s" : ""} available
                                                            </div>
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {selectedDesks.length > 0 && (
                                            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                                <p className="text-sm font-medium text-blue-900 mb-2">Agents in Selected Desks:</p>
                                                <div className="text-xs text-blue-800 space-y-1">
                                                    {Object.entries(deskAgents).map(([deskId, agents]) => {
                                                        // Normalize IDs for comparison
                                                        const normalizedDeskId = deskId?.toString();
                                                        const isSelected = selectedDesks.some(selectedId => 
                                                            selectedId?.toString() === normalizedDeskId
                                                        );
                                                        if (!isSelected) return null;
                                                        const desk = desks.find(d => {
                                                            const dId = d._id?.toString() || d._id;
                                                            return dId === normalizedDeskId;
                                                        });
                                                        return (
                                                            <div key={deskId}>
                                                                <strong>{desk?.name}:</strong> {agents.length} agent{agents.length !== 1 ? "s" : ""}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Status Selection */}
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-base flex items-center">
                                    <FilterIcon className="h-4 w-4 mr-2" />
                                    Select Statuses ({selectedStatuses.length})
                                </CardTitle>
                                <Button variant="outline" size="sm" onClick={handleSelectAllStatuses}>
                                    {selectedStatuses.length === leadStatuses.length ? "Deselect All" : "Select All"}
                                </Button>
                            </div>
                            <CardDescription>
                                Choose which lead statuses to include from your selected leads
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {leadStatuses.map((status) => (
                                    <div key={status.value} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`status-${status.value}`}
                                            checked={selectedStatuses.includes(status.value)}
                                            onCheckedChange={() => handleStatusToggle(status.value)}
                                        />
                                        <label htmlFor={`status-${status.value}`} className="text-sm cursor-pointer">
                                            {status.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Affiliate Filter (Optional) */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Filter by Affiliate (Optional)</CardTitle>
                            <CardDescription>
                                Optionally filter your selected leads by specific affiliate before splitting
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Select
                                value={selectedAffiliate}
                                onChange={setSelectedAffiliate}
                                options={[
                                    { value: "", label: "All Affiliates" },
                                    ...affiliates.map((affiliate) => ({
                                        value: affiliate,
                                        label: affiliate
                                    }))
                                ]}
                                placeholder="Select affiliate (optional)"
                            />
                        </CardContent>
                    </Card>

                    {/* Preview */}
                    <Card className="border-blue-200 bg-blue-50">
                        <CardContent className="pt-4">
                            <div className="flex items-start space-x-2">
                                <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium">Split Preview:</p>
                                    <ul className="mt-1 space-y-1">
                                        <li>
                                            • {selectedStatuses.length === 0 
                                                ? "0 leads will be split (no status selected)"
                                                : selectedLeads.length > 0 && selectedLeads[0].status === undefined 
                                                    ? (
                                                        <>
                                                            {countLoading ? (
                                                                <span className="inline-flex items-center">
                                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                                                                    Counting leads with status: {selectedStatuses.map(s => {
                                                                        const statusConfig = leadStatuses.find(ls => ls.value === s);
                                                                        return statusConfig?.label || s;
                                                                    }).join(', ')}...
                                                                </span>
                                                            ) : actualCount !== null ? (
                                                                <span>
                                                                    <strong className="text-lg text-blue-700">{actualCount.toLocaleString()}</strong> leads with status: {selectedStatuses.map(s => {
                                                                        const statusConfig = leadStatuses.find(ls => ls.value === s);
                                                                        return statusConfig?.label || s;
                                                                    }).join(', ')} will be split (from {selectedLeads.length.toLocaleString()} selected)
                                                                </span>
                                                            ) : (
                                                                <span>
                                                                    Only leads with status: {selectedStatuses.map(s => {
                                                                        const statusConfig = leadStatuses.find(ls => ls.value === s);
                                                                        return statusConfig?.label || s;
                                                                    }).join(', ')} will be split (from {selectedLeads.length.toLocaleString()} selected)
                                                                </span>
                                                            )}
                                                        </>
                                                    )
                                                    : `${filteredLeads.length.toLocaleString()} leads will be split (from ${selectedLeads.length.toLocaleString()} selected)`
                                            }
                                        </li>
                                        {shuffleType === "agents" ? (
                                            <>
                                                <li>• Among {selectedAgents.length} selected agents</li>
                                                <li>• Leads will be evenly distributed</li>
                                                <li>• No lead will remain with its current agent</li>
                                                {leadsDistribution && leadsDistribution.length > 0 && (
                                                    <li className="mt-2">
                                                        <div className="font-medium mb-1">Distribution per agent:</div>
                                                        <div className="ml-2 space-y-0.5">
                                                            {leadsDistribution.map((dist) => (
                                                                <div key={dist.agentId} className="text-xs">
                                                                    • {dist.agentName}: <strong>{dist.count.toLocaleString()}</strong> lead{dist.count !== 1 ? 's' : ''}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </li>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <li>• Among {selectedDesks.length} selected desks</li>
                                                <li>• Leads will be assigned to random agents within selected desks</li>
                                                <li>• Leads will be evenly distributed across desks</li>
                                            </>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Warning */}
                    <Card className="border-yellow-200 bg-yellow-50">
                        <CardContent className="pt-4">
                            <div className="flex items-start space-x-2">
                                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium">Important Notes:</p>
                                    <ul className="mt-1 list-disc list-inside space-y-1">
                                        <li>This action cannot be undone</li>
                                        <li>Only selected leads with assigned agents will be split</li>
                                        <li>Each lead will be assigned to a different agent than its current one</li>
                                        <li>The distribution will be as even as possible across selected agents</li>
                                        <li>Leads not matching your filter criteria will remain unchanged</li>
                                    </ul>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Split Progress Bar */}
                    {loading && shuffleProgress.total > 0 && (
                        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg">
                            <CardContent className="pt-4">
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center space-x-3">
                                            <div className="relative">
                                                <Split className="h-5 w-5 text-blue-600 animate-spin" style={{ animationDuration: '2s' }} />
                                                <div className="absolute inset-0 bg-blue-400 rounded-full animate-ping opacity-20"></div>
                                            </div>
                                            <div>
                                                <span className="text-sm font-semibold text-blue-900 block">
                                                    Splitting leads in progress
                                                </span>
                                                <span className="text-xs text-blue-600">
                                                    Please wait, processing {shuffleProgress.total.toLocaleString()} leads...
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-lg font-bold text-blue-700 block">
                                                {shuffleProgress.percentage}%
                                            </span>
                                            <span className="text-xs text-blue-600">
                                                {shuffleProgress.current.toLocaleString()} / {shuffleProgress.total.toLocaleString()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-blue-200 rounded-full h-3 shadow-inner overflow-hidden">
                                        <div
                                            className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500 ease-out relative overflow-hidden"
                                            style={{ width: `${shuffleProgress.percentage}%` }}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                </div>

                {/* Action Buttons - Sticky Footer */}
                <div className="flex space-x-3 pt-4 border-t border-blue-200 bg-gradient-to-r from-blue-50/50 to-white flex-shrink-0 -mx-6 -mb-6 px-6 pb-6">
                    <Button
                        onClick={handleShuffle}
                        disabled={
                            loading || 
                            countLoading ||
                            (shuffleType === "agents" && selectedAgents.length < 2) ||
                            (shuffleType === "desks" && selectedDesks.length < 2) ||
                            selectedStatuses.length === 0 ||
                            selectedLeads.length === 0 ||
                            (actualCount === 0 && selectedLeads.length > 0 && selectedLeads[0].status === undefined)
                        }
                        className="flex-1 bg-blue-600 hover:bg-blue-700:bg-blue-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Splitting...
                            </>
                        ) : (
                            <>
                                <Split className="h-4 w-4 mr-2" />
                                {selectedStatuses.length === 0 
                                    ? "Select Status to Split"
                                    : shuffleType === "agents" 
                                        ? (selectedLeads.length > 0 && selectedLeads[0].status === undefined 
                                            ? (actualCount !== null ? `Split ${actualCount.toLocaleString()} Leads` : `Split Leads`)
                                            : `Split ${filteredLeads.length.toLocaleString()} Leads`)
                                        : (selectedLeads.length > 0 && selectedLeads[0].status === undefined 
                                            ? (actualCount !== null ? `Split ${actualCount.toLocaleString()} Leads Between Desks` : `Split Leads Between Desks`)
                                            : `Split ${filteredLeads.length.toLocaleString()} Leads Between Desks`)
                                }
                            </>
                        )}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                        className="flex-1"
                    >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
